import yup from "src/validators/yup.Module";
import { PessoaDto } from "../../Entidade/Pessoa/Pessoa.Dto";
import { ProdutoNFCeDto } from "../NFCe/NFCe.Dto";

export interface NFeFiltrarDto {
  codigoStatusEnvio: string | null;
  dataEmissaoInicial: Date | null;
  dataEmissaoFinal: Date | null;
  serie: number | null;
}

export const NFeFiltrarSchema = yup.object().shape({
  nome: yup
    .string()
    .notRequired()
    .min(3, "O nome deve ter no mínimo 3 caracteres"),
});

export interface NFeDto {
  id: number;
  numeroNF: number;
  serie: number;
  emissaoDataHora: string;
  saidaEntradaDataHora: string | null;
  tipoOperacao: number;
  tributacaoId: number;
  finalidadeEmissao: number;
  referencia?: NFeCadastroReferenciaDto[];
  destinoOperacao: number;
  destinatario: PessoaDto;
  itens: ProdutoNFCeDto[];
  pagamento: NFeCadastroMeioPagamentoDto[];
  transporte: NFeCadastroTransporteDto;
  informacoesAdicionais: NFeCadastroInformacoesAdicionaisDto;
  consumidorFinal: number;
  total: TotalNFe;
  totalIBSCBS?: TotalIbsCbsDto | null;
  tributacaoNome?: string;
  autorizacao: NFeAutorizacaoDto;
}

export interface TotalIbsCbsDto {
  id: number;
  nFeId?: number;
  valorBCIbsCbs: number;
  valorIbs: number;
  valorCbs: number;
  valorUFIbs: number;
}


export interface NFeCadastroTransporteDto extends PessoaDto {
  modalidadeFrete?: number;
  placa: string;
  placaUF: string;
  volume: NFeCadastroVolumesDto[];
}

export interface NFeCadastroVolumesDto {
  quantidadeVolumes: number;
  especie: string;
  marca: string;
  pesoLiquido: number;
  pesoBruto: number;
}

export interface NFeCadastroInformacoesAdicionaisDto {
  id?: number;
  informacoesAdicionaisFisco?: string | null;
  informacoesComplementaresContribuinte?: string | null;
  mensagemTributacao?: string | null;
  informacoesComplementaresCompletas?: string | null;
}

export interface NFeCadastroMeioPagamentoDto {
  meioPagamento: number;
  valorPagamento: number;
  tipoIntegracao?: number | null;
  descricaoMeioPagamento?: string | null;
}

export interface TotalNFe {
  id: number;
  nFeId: number;
  valorBC: number;
  valorICMS: number;
  valorICMSDeson: number;
  valorFCPUFDest: number;
  valorICMSUFDest: number;
  valorICMSUFReme: number;
  valorFCP: number;
  valorBCST: number;
  valorST: number;
  valorFCPST: number;
  valorFCPSTRet: number;
  valorProduto: number;
  valorFrete: number;
  valorSeguro: number;
  valorDesconto: number;
  valorII: number;
  valorIPI: number;
  valorIPIDevol: number;
  valorPis: number;
  valorCofins: number;
  valorOutro: number;
  valorNotaFiscal: number;
  valorAproxTributos: number;
  valorTroco: number;
}

export interface NFeAutorizacaoDto {
  codigoStatusEnvio: number;
  descricaoStatusEnvio: string;
  codigoStatusResposta: number;
  descricaoMotivoResposta: string;
}

export interface NFeCadastroReferenciaDto {
  chaveAcesso: string;
}

export const STORAGENFE_KEY = "nfeTemp";

export function updateLocal<T extends object>(
  key: string,
  partial: Partial<T> | null
) {
  if (partial === null) {
    localStorage.removeItem(key);
    return;
  }

  const stored = localStorage.getItem(key);
  const current: T = stored ? JSON.parse(stored) : ({} as T);

  const updated: T = {
    ...current,
    ...partial,
  };

  localStorage.setItem(key, JSON.stringify(updated));
}

export function getLocal<T>(key: string): T | null {
  const stored = localStorage.getItem(key);
  return stored ? (JSON.parse(stored) as T) : null;
}

export enum EStatusEnvioNFe {
  AguardandoEnvio = 0,
  EnviadaSemRetorno = 1,
  Finalizada = 2,
  FinalizadaDenegada = 3,
  FinalizadaCancelada = 4,
}

export const statusEnvioNFeOptions = [
  { value: "99", label: "Todos os Status" },
  { value: "0", label: "0 - Aguardando Envio" },
  { value: "1", label: "1 - Enviada Sem Retorno" },
  { value: "2", label: "2 - Finalizada" },
  { value: "3", label: "3 - Finalizada Denegada" },
  { value: "4", label: "4 - Finalizada Cancelada" },
  { value: "3,4", label: "Canceladas / Denegadas" },
];

export const statusEnvioNFeDescricao: Record<number, string> = {
  [EStatusEnvioNFe.AguardandoEnvio]: "Aguardando Envio",
  [EStatusEnvioNFe.EnviadaSemRetorno]: "Enviada Sem Retorno",
  [EStatusEnvioNFe.Finalizada]: "Finalizada",
  [EStatusEnvioNFe.FinalizadaDenegada]: "Finalizada Denegada",
  [EStatusEnvioNFe.FinalizadaCancelada]: "Finalizada Cancelada",
};

export interface VisualStatusConfigNFe {
  categoria:
    | "nao_enviado"
    | "enviado_autorizado"
    | "cancelado"
    | "rejeitado"
    | "em_processamento";
  label: string;
  badgeBg: string;
  badgeText?: string;
  borderClass: string;
  iconSymbol: string;
}

export const getVisualStatusNFe = (item: NFeDto): VisualStatusConfigNFe => {
  const aut = item.autorizacao;

  // 1. Se não possui objeto de autorização ou não tem status
  if (!aut || aut.codigoStatusEnvio === undefined || aut.codigoStatusEnvio === null) {
    return {
      categoria: "nao_enviado",
      label: "Não Enviada",
      badgeBg: "warning",
      badgeText: "dark",
      borderClass: "border-warning",
      iconSymbol: "⏳",
    };
  }

  const codEnvio = Number(aut.codigoStatusEnvio);
  const codResp = Number(aut.codigoStatusResposta);
  const descEnvio = (aut.descricaoStatusEnvio || "").toLowerCase();
  const descResp = (aut.descricaoMotivoResposta || "").toLowerCase();

  // 2. Cancelada
  if (
    codEnvio === EStatusEnvioNFe.FinalizadaCancelada ||
    codEnvio === 4 ||
    codResp === 101 ||
    codResp === 135 ||
    codResp === 136 ||
    descEnvio.includes("cancelad") ||
    descResp.includes("cancelad")
  ) {
    return {
      categoria: "cancelado",
      label: "Cancelada",
      badgeBg: "danger",
      badgeText: "white",
      borderClass: "border-danger",
      iconSymbol: "✕",
    };
  }

  // 3. Denegada
  if (
    codEnvio === EStatusEnvioNFe.FinalizadaDenegada ||
    codEnvio === 3 ||
    [110, 301, 302, 303, 304, 305].includes(codResp) ||
    descEnvio.includes("denegad") ||
    descResp.includes("denegad")
  ) {
    return {
      categoria: "rejeitado",
      label: "Denegada",
      badgeBg: "danger",
      badgeText: "white",
      borderClass: "border-danger",
      iconSymbol: "⚠",
    };
  }

  // 4. Enviada e Autorizada / Finalizada
  if (
    codResp === 100 ||
    codEnvio === EStatusEnvioNFe.Finalizada ||
    codEnvio === 2
  ) {
    return {
      categoria: "enviado_autorizado",
      label: "Enviada e Autorizada",
      badgeBg: "success",
      badgeText: "white",
      borderClass: "border-success",
      iconSymbol: "✓",
    };
  }

  // 5. Rejeitada pela SEFAZ
  if (codResp > 100) {
    return {
      categoria: "rejeitado",
      label: "Rejeitada pela SEFAZ",
      badgeBg: "danger",
      badgeText: "white",
      borderClass: "border-danger",
      iconSymbol: "⚠",
    };
  }

  // 6. Enviada sem retorno / Em transmissão
  if (codEnvio === EStatusEnvioNFe.EnviadaSemRetorno || codEnvio === 1) {
    return {
      categoria: "em_processamento",
      label: "Em Transmissão",
      badgeBg: "info",
      badgeText: "white",
      borderClass: "border-info",
      iconSymbol: "🔄",
    };
  }

  // 7. Aguardando Envio (0 ou padrão)
  return {
    categoria: "nao_enviado",
    label: "Aguardando Envio",
    badgeBg: "warning",
    badgeText: "dark",
    borderClass: "border-warning",
    iconSymbol: "⏳",
  };
};
